/**
 * ConsoleGridPage.jsx · v2.7.63+E48
 * ─────────────────────────────────────────────────────────────────────
 * Seamless single-window 2×2 grid layout hosting the four dispatch
 * consoles (Live Map, Chat, Helpdesk, Emails) inside one browser window.
 * Designed for the PWA "standalone" launch — no address bar, no tabs,
 * completely native-app feel.
 *
 * Each panel is an <iframe> pointing at the corresponding admin route
 * with `?console=<id>` appended. Every panel exposes:
 *   • Pop-Out button — detaches that single panel to its own dedicated
 *     window (uses the same URL)
 *   • Fullscreen button — expands the panel to fill this window (via
 *     the native Fullscreen API + local grid-item toggle)
 *   • Grabbable title bar — displays the operator's identity via the
 *     OperatorBadge which reads `?console=<id>` from the URL
 *
 * The wrapper page loads the dedicated console PWA manifest so the
 * launcher can install it as its own app icon on desktop / mobile.
 */
import { useEffect, useRef, useState } from "react";
import OperatorBadge from "@/components/admin/OperatorBadge";
import { Maximize2, Minimize2, ExternalLink, Grid3x3, RefreshCw } from "lucide-react";

const PANELS = [
  { id: "map",      title: "Live Dispatch Map", path: "/admin?console=map",           icon: "🗺",  accent: "amber"   },
  { id: "chat",     title: "Chat",              path: "/admin/chat?console=chat",     icon: "💬", accent: "emerald" },
  { id: "helpdesk", title: "Helpdesk",          path: "/admin/helpdesk?console=helpdesk", icon: "🎟", accent: "sky"    },
  { id: "email",    title: "Emails",            path: "/admin/email?console=email",   icon: "✉", accent: "purple"  },
];


export default function ConsoleGridPage() {
  const [maximized, setMaximized] = useState(null);   // panel id currently maximized OR null
  const [reloadKey, setReloadKey] = useState({});     // per-panel remount trigger

  const bump = (id) => setReloadKey((s) => ({ ...s, [id]: (s[id] || 0) + 1 }));

  // Hide any browser chrome hints that leak in on non-standalone launches.
  // v2.7.63+E48 · also inject the console-specific PWA manifest at runtime
  // (React Helmet not available in this codebase) so the browser sees this
  // route as an installable standalone app with its own icon + name.
  useEffect(() => {
    document.body.dataset.gridConsole = "true";
    document.title = "Grid Console · Buller Taxi Dispatch";
    // v2.7.63+E48.1 · Query DOM fresh in both mount AND unmount (do NOT
    // cache the node references) so React StrictMode double-mount in
    // dev won't accidentally re-inject stale <link> nodes. Track the
    // hrefs we hid so we can rehydrate on unmount from head reality.
    const injected = document.createElement("link");
    injected.rel  = "manifest";
    injected.href = "/console-manifest.json";
    injected.id   = "console-grid-manifest";
    // Snapshot the default manifest href(s) BEFORE removing so we can
    // recreate equivalent nodes on cleanup — no reference retention.
    const priorHrefs = Array.from(document.querySelectorAll('link[rel="manifest"]'))
      .filter((l) => l.id !== "console-grid-manifest")
      .map((l) => l.getAttribute("href"))
      .filter(Boolean);
    Array.from(document.querySelectorAll('link[rel="manifest"]'))
      .filter((l) => l.id !== "console-grid-manifest")
      .forEach((l) => l.remove());
    document.head.appendChild(injected);
    const themeMeta = document.createElement("meta");
    themeMeta.name = "theme-color";
    themeMeta.content = "#f59e0b";
    themeMeta.id = "console-grid-theme";
    document.head.appendChild(themeMeta);
    return () => {
      delete document.body.dataset.gridConsole;
      document.getElementById("console-grid-manifest")?.remove();
      document.getElementById("console-grid-theme")?.remove();
      // Rebuild the default manifest link fresh (avoids duplicates on
      // rapid re-mount under React StrictMode).
      const alreadyHas = new Set(
        Array.from(document.querySelectorAll('link[rel="manifest"]')).map((l) => l.getAttribute("href")),
      );
      priorHrefs.forEach((href) => {
        if (alreadyHas.has(href)) return;
        const l = document.createElement("link");
        l.rel = "manifest";
        l.href = href;
        document.head.appendChild(l);
      });
    };
  }, []);

  return (
    <>
      {/* Operator identity pill (fixed top-right). Reads ?standalone / */}
      <OperatorBadge/>

      <div
        className="fixed inset-0 bg-zinc-950 text-zinc-100 flex flex-col"
        data-testid="console-grid-page"
      >
        {/* Slim header — reserved so operators can always spot the
            fingerprint of the window at a glance without opening the
            operator badge. Also hosts a Reload-All action. */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-900 border-b border-amber-500/25 select-none">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-300 font-extrabold">
            <Grid3x3 className="h-3.5 w-3.5"/>
            <span>Fleet · 4-Window Grid Console</span>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-zinc-500">
            <button
              type="button"
              onClick={() => PANELS.forEach((p) => bump(p.id))}
              className="hover:text-zinc-200 flex items-center gap-1"
              data-testid="grid-reload-all"
              title="Reload all four panels"
            >
              <RefreshCw className="h-3 w-3"/>Reload All
            </button>
            <span className="opacity-70">Auth persists across panels</span>
          </div>
        </div>

        {/* Grid area — 2×2 fills the remaining viewport. When one panel
            is maximized we swap to a single-cell layout so it dominates
            the window (mirrors the browser Fullscreen API for cases
            when the OS chrome must stay visible). */}
        <div
          className={`flex-1 grid gap-1 p-1 ${
            maximized
              ? "grid-cols-1 grid-rows-1"
              : "grid-cols-2 grid-rows-2"
          }`}
        >
          {PANELS.map((p) => {
            if (maximized && maximized !== p.id) return null;
            return (
              <GridPanel
                key={p.id}
                panel={p}
                maximized={maximized === p.id}
                onMaximizeToggle={() => setMaximized((m) => (m === p.id ? null : p.id))}
                onReload={() => bump(p.id)}
                reloadKey={reloadKey[p.id] || 0}
              />
            );
          })}
        </div>
      </div>
    </>
  );
}


function GridPanel({ panel, maximized, onMaximizeToggle, onReload, reloadKey }) {
  const iframeRef = useRef(null);
  const accent = {
    amber:   "border-amber-500/40 text-amber-200",
    emerald: "border-emerald-500/40 text-emerald-200",
    sky:     "border-sky-500/40 text-sky-200",
    purple:  "border-purple-500/40 text-purple-200",
  }[panel.accent];

  const popOut = () => {
    // Match the seamless-window feature string from the spec so the
    // detached window loses all browser chrome the same way the main
    // grid console does. Dimensions default to Full-HD; browser will
    // clamp to available screen area.
    const w = Math.min(1920, window.screen.availWidth  || 1920);
    const h = Math.min(1080, window.screen.availHeight || 1080);
    window.open(
      panel.path,
      `GridPanel_${panel.id}`,
      `width=${w},height=${h},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`,
    );
  };

  return (
    <div
      className={`bg-zinc-950 border rounded-md overflow-hidden flex flex-col ${accent}`}
      data-testid={`grid-panel-${panel.id}`}
    >
      <div className="flex items-center justify-between px-2 py-1 bg-zinc-900/90 border-b border-zinc-800 select-none">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest">
          <span className="text-base leading-none">{panel.icon}</span>
          <span>{panel.title}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onReload}
            className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition"
            title="Reload this panel"
            data-testid={`grid-panel-${panel.id}-reload`}
          >
            <RefreshCw className="h-3.5 w-3.5"/>
          </button>
          <button
            type="button"
            onClick={popOut}
            className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition"
            title="Pop out to dedicated window"
            data-testid={`grid-panel-${panel.id}-popout`}
          >
            <ExternalLink className="h-3.5 w-3.5"/>
          </button>
          <button
            type="button"
            onClick={onMaximizeToggle}
            className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition"
            title={maximized ? "Restore" : "Maximize in grid"}
            data-testid={`grid-panel-${panel.id}-maximize`}
          >
            {maximized ? <Minimize2 className="h-3.5 w-3.5"/> : <Maximize2 className="h-3.5 w-3.5"/>}
          </button>
        </div>
      </div>
      <iframe
        ref={iframeRef}
        key={reloadKey}
        src={panel.path}
        title={panel.title}
        className="flex-1 w-full border-0 bg-zinc-950"
        data-testid={`grid-panel-${panel.id}-iframe`}
      />
    </div>
  );
}
